// contexts/WorkshopContext.tsx
"use client";

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useUser } from "./UserContext";
import { apiRemote } from "@/api";

interface WorkshopContextType {
  registeredWorkshops: any[];
  loadingWorkshops: boolean;
  loadRegisteredWorkshops: () => Promise<void>;
}

const WorkshopContext = createContext<WorkshopContextType | undefined>(
  undefined
);

export const WorkshopProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser();
  const [registeredWorkshops, setRegisteredWorkshops] = useState<any[]>([]);
  const [loadingWorkshops, setLoadingWorkshops] = useState<boolean>(false);

  const loadRegisteredWorkshops = useCallback(async () => {
    setLoadingWorkshops(true);
    try {
      const response = await apiRemote.getUserRegisteredWorkshop(
        user?.user_login
      );
      if (response.data && Array.isArray(response.data)) {
        setRegisteredWorkshops(response.data);
      } else {
        setRegisteredWorkshops([]);
      }
    } catch (error) {
      console.error("Error loading registered workshops:", error);
      setRegisteredWorkshops([]);
    } finally {
      setLoadingWorkshops(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.user_login) {
      loadRegisteredWorkshops();
    }
  }, [user?.user_login, loadRegisteredWorkshops]);

  return (
    <WorkshopContext.Provider
      value={{ registeredWorkshops, loadingWorkshops, loadRegisteredWorkshops }}
    >
      {children}
    </WorkshopContext.Provider>
  );
};

export const useWorkshop = () => {
  const context = useContext(WorkshopContext);
  if (context === undefined) {
    throw new Error("useWorkshop must be used within a WorkshopProvider");
  }
  return context;
};